import { Container } from '@studio/design-system/select';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, χref } from '@studio/react-utils/hooks';
import { toEnumValues } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'cn';
import { ActionBox, Heading } from '../layout/index.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/index.js';
import { useHotKeysCategoryPref } from './hooks.js';
import { Section } from './section.jsx';
import type { Props } from './types.js';
import { Categories } from '#~/hot-keys/components/categories/index.js';
import * as HK from '#~/hot-keys/index.js';
import type { Action, Combo } from '#~/hot-keys/index.js';

/** `<HotKeys>` panel. */
export const HotKeys = ({ className, ...props }: Props): ReactElement => {
  const category = toAccessor(useHotKeysCategoryPref());
  const containerRef = useRef<HTMLDivElement>(null);

  const hotKeys = useStore((µ) => µ[Panel.HotKeys].hotKeys);
  const producer = useProducer();

  const formula = useMemo(() => HK.toFormula(hotKeys), [hotKeys]);

  const handleUpdate = useLatestFn((action: Action, combo: Combo | false) => {
    producer((draft) => {
      draft[Panel.HotKeys].hotKeys[action] = combo;
    });
  });

  const handleScroll = useLatestFn(() => {
    const container = χref(containerRef);

    if (!container) {
      return;
    }

    const section = container.querySelector(`[data-category="${category.value}"]`);

    if (section) {
      container.scrollTo({ behavior: 'smooth', top: (section as HTMLElement).offsetTop });
    }
  });

  // Manage panel state.
  useEffect(() => {
    producer((draft) => {
      const data = draft[Panel.HotKeys];
      // Check if the current set of hot keys is different that the snapshot.
      const isDirty = !dequal(hotKeys, data.snapshot);

      if (data.isDirty !== isDirty) {
        data.isDirty = isDirty;
      }

      // Check if the current set of hot keys is different that the defaults.
      const isDefault = dequal(hotKeys, HK.DefaultHotKeys);

      if (data.isDefault !== isDefault) {
        data.isDefault = isDefault;
      }
    });
  }, [hotKeys]);

  useEffect(() => {
    handleScroll();
  }, [category.value]);

  return (
    <div className={twMerge('flex h-full flex-col', className)} {...props}>
      <ActionBox>
        <Heading
          subtitle='Make your workflow more efficient by using and customizing Hot Keys.'
          title='Hot Keys'
        />
      </ActionBox>

      <Container className='my-16 max-w-[245px]' label='Categories'>
        <Categories category={category} />
      </Container>

      <div className='relative mt-16 flex grow flex-col gap-40 overflow-auto' ref={containerRef}>
        {toEnumValues(HK.Category)
          .sort((a, b) => HK.Categories[a].order - HK.Categories[b].order)
          .map((µ) => (
            <div data-category={µ} key={µ}>
              <h3 className='mb-24 text-lg font-demibold'>{HK.Categories[µ].label}</h3>
              <Section category={µ} formula={formula} hotKeys={hotKeys} onCommit={handleUpdate} />
            </div>
          ))}
      </div>
    </div>
  );
};
